import {
  Button,
  TickS,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useTranslation } from "react-i18next";

import {
  StyledCategoryBadge,
  StyledLoadButton,
  StyledLoadWarningText,
  StyledPreviewGrid,
  StyledPreviewHeader,
  StyledPreviewItem,
  StyledPreviewLabel,
  StyledPreviewTitle,
  StyledPreviewValue,
  StyledTemplatePreview,
  StyledUnavailableBanner,
} from "./styles";
import { PreviewTemplateProps, TemplatePreviewData } from "./types";
import { parseTemplatePreview } from "./utils";

const PreviewTemplate = (props: PreviewTemplateProps) => {
  const { templateData, variant } = props;
  const { t: tFormData } = useTranslation("formData");
  const previewData = parseTemplatePreview(templateData.rawFormData);
  const accountSummary =
    variant === "creation" ? props.accountSummary : undefined;
  const mergedPreviewData: TemplatePreviewData = {
    ...previewData,
    ...(accountSummary ?? {}),
  };
  const fieldsToDisplay =
    variant === "modification"
      ? [
          "customerNumber",
          "registrationNumber",
          "productTypeCode",
          "vehicleType",
          "customizableLine1",
          "customizableLine2",
        ]
      : [
          "accountLabel",
          "divisionLabel",
          "ledgerTypeLabel",
          "customerNumber",
          "productTypeCode",
        ];
  const hasLoadAction = Boolean(props.onLoad);

  const labels = {
    title:
      variant === "modification"
        ? tFormData("formData.modificationTemplate.preview.title")
        : tFormData("formData.creationTemplate.preview.title"),
    unavailable:
      variant === "modification"
        ? tFormData("formData.modificationTemplate.preview.unavailable")
        : tFormData("formData.creationTemplate.preview.unavailable"),
    loadButton: tFormData("formData.modificationTemplate.buttons.load"),
    loadWarning:
      variant === "modification"
        ? tFormData("formData.modificationTemplate.preview.loadWarning")
        : tFormData("formData.creationTemplate.preview.loadWarning"),
  };

  const renderValue = (field: keyof TemplatePreviewData) =>
    mergedPreviewData[field];

  return (
    <StyledTemplatePreview data-testid='formdata__template-preview'>
      <StyledPreviewHeader>
        <StyledPreviewTitle variant='title1'>{labels.title}</StyledPreviewTitle>
        <StyledCategoryBadge>{templateData.category}</StyledCategoryBadge>
      </StyledPreviewHeader>

      {templateData.isUnavailable && (
        <StyledUnavailableBanner>
          ⚠️ {labels.unavailable}
        </StyledUnavailableBanner>
      )}

      <StyledPreviewGrid>
        {fieldsToDisplay.map(
          (field) =>
            renderValue(field as keyof TemplatePreviewData) && (
              <StyledPreviewItem key={field}>
                <StyledPreviewLabel>
                  {tFormData(`formData.${variant}Template.preview.${field}`)}
                </StyledPreviewLabel>
                <StyledPreviewValue>
                  {renderValue(field as keyof TemplatePreviewData)}
                </StyledPreviewValue>
              </StyledPreviewItem>
            ),
        )}
      </StyledPreviewGrid>

      {hasLoadAction && (
        <>
          <StyledLoadButton>
            <Button
              variant='primary'
              size='m'
              isLowercase
              leftIcon={<TickS width={25} height={25} color='white' />}
              disabled={templateData.isUnavailable}
              buttonText={labels.loadButton}
              onClick={props.onLoad}
              data-testid='formdata__template-preview-load-button'
            />
          </StyledLoadButton>
          <StyledLoadWarningText>⚠️ {labels.loadWarning}</StyledLoadWarningText>
        </>
      )}
    </StyledTemplatePreview>
  );
};

export default PreviewTemplate;
